// Global Instructions Rule Applied!
//
// Configuration → Relationship Fact Types (cfg.relationship_fact_types).
// Admin-gated lookup CRUD in the PipelineStatusesPanel shape. The PII rule
// lives here as data: a personal type can never be visible in drafts (the
// table CHECK refuses it too). Delete is refused while live facts use the type.
import React, { useCallback, useEffect, useState } from 'react'
import { Button, Input, InputNumber, Modal, Popconfirm, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock } from '@fortawesome/free-solid-svg-icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { RelationshipFactTypeAdminRow } from '../../../state_manager/data_models/relationships_model.ts'
import { fetchRelationshipFactTypes, saveRelationshipFactType, deleteRelationshipFactType } from '../../relationships/services/relationshipsService.ts'

const { Title } = Typography

export default function RelationshipFactTypesPanel() {
  const [rows, setRows] = useState<RelationshipFactTypeAdminRow[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<RelationshipFactTypeAdminRow | null>(null)
  const [typeKey, setTypeKey] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [guidance, setGuidance] = useState('')
  const [isPersonal, setIsPersonal] = useState(false)
  const [visibleInDrafts, setVisibleInDrafts] = useState(false)
  const [sortOrder, setSortOrder] = useState<number>(100)
  const [enabled, setEnabled] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchRelationshipFactTypes(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load fact types')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openAdd = () => {
    setEditing(null); setTypeKey(''); setLabel(''); setDescription(''); setGuidance(''); setIsPersonal(false)
    setVisibleInDrafts(false); setSortOrder(100); setEnabled(true); setOpen(true)
  }
  const openEdit = (r: RelationshipFactTypeAdminRow) => {
    setEditing(r); setTypeKey(r.relationship_fact_types__type_key); setLabel(r.relationship_fact_types__label)
    setDescription(r.relationship_fact_types__description ?? ''); setGuidance(r.relationship_fact_types__guidance ?? '')
    setIsPersonal(r.relationship_fact_types__is_personal); setVisibleInDrafts(r.relationship_fact_types__visible_in_drafts)
    setSortOrder(r.relationship_fact_types__sort_order); setEnabled(r.relationship_fact_types__enabled); setOpen(true)
  }

  const submit = async () => {
    setSubmitting(true)
    try {
      await saveRelationshipFactType(
        editing ? editing.relationship_fact_types__id : null,
        { type_key: typeKey.trim(), label: label.trim(), description: description.trim() || null, guidance: guidance.trim() || null,
          is_personal: isPersonal, visible_in_drafts: isPersonal ? false : visibleInDrafts, sort_order: sortOrder, enabled },
        token()
      )
      toast.success(editing ? 'Fact type updated' : 'Fact type added')
      setOpen(false)
      await load()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async (r: RelationshipFactTypeAdminRow) => {
    setDeletingId(r.relationship_fact_types__id)
    try {
      await deleteRelationshipFactType(r.relationship_fact_types__id, token())
      toast.success('Fact type deleted')
      await load()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  const columns: ColumnsType<RelationshipFactTypeAdminRow> = [
    { title: 'Key', dataIndex: 'relationship_fact_types__type_key', key: 'key', width: 190, render: (v: string) => <code>{v}</code> },
    { title: 'Label', dataIndex: 'relationship_fact_types__label', key: 'label', width: 190,
      render: (v: string, r) => (<span className='inline-flex items-center gap-1'>{r.relationship_fact_types__is_personal ? <Tooltip title='Personal — in-app only'><FontAwesomeIcon icon={faLock} className='text-purple-600' /></Tooltip> : null}{v}</span>) },
    { title: 'Description', dataIndex: 'relationship_fact_types__description', key: 'desc', ellipsis: true, render: (v: string | null) => v || '' },
    { title: 'Personal', dataIndex: 'relationship_fact_types__is_personal', key: 'personal', width: 100, render: (v: boolean) => (v ? <Tag color='purple'>in-app only</Tag> : '') },
    { title: 'In drafts', dataIndex: 'relationship_fact_types__visible_in_drafts', key: 'drafts', width: 100, render: (v: boolean) => (v ? <Tag color='blue'>may cite</Tag> : '') },
    { title: 'Order', dataIndex: 'relationship_fact_types__sort_order', key: 'order', width: 70 },
    { title: 'Enabled', dataIndex: 'relationship_fact_types__enabled', key: 'enabled', width: 90, render: (v: boolean) => (v ? <Tag color='green'>enabled</Tag> : <Tag>disabled</Tag>) },
    { title: 'In use', dataIndex: 'prog__usage_count', key: 'usage', width: 70 },
    {
      title: '', key: 'actions', width: 90, align: 'right',
      render: (_: unknown, r) => (
        <span className='inline-flex gap-1'>
          <Button type='text' size='small' icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title='Delete this fact type?' okText='Delete' onConfirm={() => onDelete(r)} disabled={r.prog__usage_count > 0}>
            <Button type='text' danger size='small' icon={<DeleteOutlined />} disabled={r.prog__usage_count > 0} loading={deletingId === r.relationship_fact_types__id} />
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <div className='flex flex-col h-full min-h-0'>
      <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
        <Title level={4} style={{ margin: 0 }}>Relationship Fact Types</Title>
        <Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>New Fact Type</Button>
      </div>
      <div className='flex-1 min-h-0 overflow-auto'>
        <Table size='small' rowKey='relationship_fact_types__id' columns={columns} dataSource={rows} loading={loading} pagination={false} />
      </div>
      <Modal title={editing ? 'Edit fact type' : 'New fact type'} open={open} okText={editing ? 'Save' : 'Add'}
        okButtonProps={{ disabled: !label.trim() || (!editing && !typeKey.trim()) }} confirmLoading={submitting}
        onOk={submit} onCancel={() => setOpen(false)} destroyOnClose>
        <div className='flex flex-col gap-3 pt-2'>
          <div><div className='text-xs text-gray-600 mb-1'>Key (snake_case; fixed once created)</div>
            <Input value={typeKey} onChange={(e) => setTypeKey(e.target.value)} disabled={!!editing} placeholder='e.g. interest_professional' /></div>
          <div><div className='text-xs text-gray-600 mb-1'>Label</div><Input value={label} onChange={(e) => setLabel(e.target.value)} /></div>
          <div><div className='text-xs text-gray-600 mb-1'>Description</div><Input.TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div><div className='text-xs text-gray-600 mb-1'>Guidance for the agent (what to record under this type)</div><Input.TextArea rows={2} value={guidance} onChange={(e) => setGuidance(e.target.value)} /></div>
          <div className='flex flex-col gap-2'>
            <span className='inline-flex items-center gap-2 text-sm'><Switch checked={isPersonal} onChange={(v) => { setIsPersonal(v); if (v) setVisibleInDrafts(false) }} /> Personal (in-app only; never in drafts, briefs or reports)</span>
            <span className='inline-flex items-center gap-2 text-sm'><Switch checked={visibleInDrafts} disabled={isPersonal} onChange={setVisibleInDrafts} /> Agents may cite it in drafts</span>
            <span className='inline-flex items-center gap-2 text-sm'><Switch checked={enabled} onChange={setEnabled} /> Enabled</span>
          </div>
          <div><div className='text-xs text-gray-600 mb-1'>Sort order</div><InputNumber value={sortOrder} onChange={(v) => setSortOrder(Number(v ?? 100))} /></div>
        </div>
      </Modal>
    </div>
  )
}
